import React, { useEffect, useState } from 'react';
import { 
    Card, 
    Table, 
    Tag, 
    Button, 
    Select, 
    Space, 
    Typography, 
    Row, 
    Col, 
    Statistic,
    Input,
    DatePicker,
    message
} from 'antd';
import { 
    EyeOutlined, 
    EditOutlined, 
    SearchOutlined,
    CalendarOutlined,
    DollarOutlined,
    UserOutlined
} from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

function VendorBookings() {
    const { user } = useAuth();
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updatingId, setUpdatingId] = useState(null);
    const [filters, setFilters] = useState({
        status: '',
        dateRange: null,
        search: ''
    });

    const statusOptions = [
        { label: 'All Status', value: '' },
        { label: 'Pending', value: 'pending' },
        { label: 'Confirmed', value: 'confirmed' },
        { label: 'Preparing', value: 'preparing' },
        { label: 'Ready', value: 'ready' },
        { label: 'Delivered', value: 'delivered' },
        { label: 'Cancelled', value: 'cancelled' }
    ];

    useEffect(() => {
        fetchBookings();
    }, [filters]);

    const fetchBookings = async () => {
        try {
            setLoading(true);
            const params = {
                per_page: 50,
                ...(filters.status && { status: filters.status }),
                ...(filters.search && { search: filters.search })
            };

            const response = await api.getBookings(params);
            if (response.success) {
                let bookingsData = response.data.data || [];
                
                // Filter by date range if selected
                if (filters.dateRange && filters.dateRange.length === 2) {
                    const [startDate, endDate] = filters.dateRange;
                    bookingsData = bookingsData.filter(booking => {
                        const bookingDate = dayjs(booking.event_date);
                        return bookingDate.isAfter(startDate.subtract(1, 'day')) && 
                               bookingDate.isBefore(endDate.add(1, 'day'));
                    });
                }
                
                setBookings(bookingsData);
            }
        } catch (error) {
            message.error('Failed to fetch bookings');
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (bookingId, status) => {
        try {
            setUpdatingId(bookingId);
            await api.updateBookingStatus(bookingId, status);
            message.success('Booking status updated successfully');
            fetchBookings();
        } catch (error) {
            message.error('Failed to update booking status');
        } finally {
            setUpdatingId(null);
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'orange',
            confirmed: 'blue',
            preparing: 'purple',
            ready: 'green',
            delivered: 'green',
            cancelled: 'red'
        };
        return colors[status] || 'default';
    };

    const columns = [
        {
            title: 'Booking #',
            dataIndex: 'booking_number',
            key: 'booking_number',
            render: (text) => <Text strong>#{text}</Text>
        },
        {
            title: 'Customer',
            dataIndex: 'user',
            key: 'customer',
            render: (user) => (
                <div>
                    <div className="font-medium">{user?.name}</div>
                    <div className="text-sm text-gray-500">{user?.email}</div>
                </div>
            )
        },
        {
            title: 'Event Details',
            key: 'event',
            render: (_, record) => (
                <div>
                    <div className="font-medium">{record.event_type || 'Event'}</div>
                    <div className="text-sm text-gray-600">
                        <CalendarOutlined className="mr-1" />
                        {dayjs(record.event_date).format('MMM DD, YYYY')} at {record.event_time}
                    </div>
                    <div className="text-sm text-gray-600">
                        <UserOutlined className="mr-1" />
                        {record.guest_count} guests
                    </div>
                </div>
            )
        },
        {
            title: 'Venue',
            dataIndex: 'venue',
            key: 'venue',
            ellipsis: true
        },
        {
            title: 'Amount',
            dataIndex: 'total_amount',
            key: 'amount',
            render: (amount) => (
                <div className="text-right">
                    <div className="font-medium text-green-600">
                        <DollarOutlined className="mr-1" />
                        ₹{amount?.toLocaleString() || '0'}
                    </div>
                </div>
            )
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={getStatusColor(status)}>
                    {status?.toUpperCase()}
                </Tag>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Select
                        size="small"
                        value={record.status}
                        onChange={(value) => updateStatus(record.id, value)}
                        loading={updatingId === record.id}
                        style={{ minWidth: 120 }}
                    >
                        {statusOptions.slice(1).map(option => (
                            <Select.Option key={option.value} value={option.value}>
                                {option.label}
                            </Select.Option>
                        ))}
                    </Select>
                </Space>
            )
        }
    ];

    const stats = {
        total: bookings.length,
        pending: bookings.filter(b => b.status === 'pending').length,
        confirmed: bookings.filter(b => b.status === 'confirmed').length,
        delivered: bookings.filter(b => b.status === 'delivered').length,
        revenue: bookings
            .filter(b => b.status === 'delivered')
            .reduce((sum, b) => sum + (parseFloat(b.total_amount) || 0), 0)
    };

    return (
        <div className="p-6">
            <div className="mb-6">
                <Title level={2}>Booking Management</Title>
                <Text type="secondary">Manage all customer bookings and orders</Text>
            </div>

            {/* Statistics */}
            <Row gutter={[16, 16]} className="mb-6">
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic
                            title="Total Bookings"
                            value={stats.total}
                            prefix={<CalendarOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic
                            title="Pending"
                            value={stats.pending}
                            valueStyle={{ color: '#faad14' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic
                            title="Confirmed"
                            value={stats.confirmed}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={6}>
                    <Card>
                        <Statistic
                            title="Revenue"
                            value={stats.revenue}
                            prefix="₹"
                            precision={0}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Filters */}
            <Card className="mb-6">
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={8}>
                        <Input
                            placeholder="Search by customer name or booking number"
                            prefix={<SearchOutlined />}
                            value={filters.search}
                            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={6}>
                        <Select
                            placeholder="Filter by status"
                            value={filters.status}
                            onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                            options={statusOptions}
                            style={{ width: '100%' }}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={6}>
                        <RangePicker
                            placeholder={['Start Date', 'End Date']}
                            value={filters.dateRange}
                            onChange={(dates) => setFilters(prev => ({ ...prev, dateRange: dates }))}
                            style={{ width: '100%' }}
                        />
                    </Col>
                    <Col xs={24} sm={4}>
                        <Button 
                            onClick={() => setFilters({ status: '', dateRange: null, search: '' })}
                            style={{ width: '100%' }}
                        >
                            Clear Filters
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* Bookings Table */}
            <Card>
                <Table
                    columns={columns}
                    dataSource={bookings}
                    loading={loading}
                    rowKey="id"
                    pagination={{
                        pageSize: 20,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => 
                            `${range[0]}-${range[1]} of ${total} bookings`
                    }}
                    scroll={{ x: 1000 }}
                />
            </Card>
        </div>
    );
}

export default VendorBookings;
